import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { GoToStep } from '../../../store/onboarding/onboarding.actions';
import { AuthApiService } from '../../../_services/auth-api-service/auth-api.service';
import { AppFormComponent } from '../../../shared/classes/app-form';
import { Observable } from 'rxjs';
import { TokenResponse } from '@bo/ng-auth';
import { Method } from '@bo/ng-api';
import { ReactiveFormsModule } from '@angular/forms';
import { BoFormsModule, InputType } from '@bo/ng-forms';
import { FieldComponent } from '../../../shared/components/forms/form-field/form-field.component';
import { SubmitComponent } from '../../../shared/components/forms/submit/submit.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordFieldComponent } from '../../../shared/components/forms/password-field/password-field.component';
import { OnboardingSteps } from '../../../store/onboarding/onboarding.model';
import { RouterSwitchBtnComponent } from '../../../shared/components/router-switch-btn/router-switch-btn.component';
import { ClearAppState, NavigateToDashboardFromLogin } from '../../../store/app/app.actions';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { updateParamsObject } from '../../../shared/utils/filter-utils';
import { SetSubscriptionStateFromUserLogin } from '../../../store/subscription/subscription.actions';
import { SetTokens } from '../../../store/auth/auth.actions';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    BoFormsModule,
    FieldComponent,
    SubmitComponent,
    PasswordFieldComponent,
    RouterSwitchBtnComponent,
    LoaderComponent,
  ],
  templateUrl: './login.component.html',
})
export class LoginComponent extends AppFormComponent<TokenResponse> implements OnInit, AfterViewInit {
  readonly store = inject(Store);
  readonly authService = inject(AuthApiService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);

  protected fields = ['username', 'password'];
  queryParams = new HttpParams();
  private returnUrl: string | null = null;

  override ngOnInit() {
    super.ngOnInit();
    // Capture the returnUrl from query params (set by custom guard)
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || null;
  }

  ngAfterViewInit() {
    // After logging out, clear app state first to remove previous user data.
    this.store.dispatch(new ClearAppState());
    this.store.dispatch(new GoToStep(OnboardingSteps.LOGIN));
  }

  createForm(): void {
    this.buildFromOptions(this.authService.getOptions(this.authService.URL_LOGIN, Method.POST)).subscribe();
  }

  handleSubmitSuccess(data: TokenResponse): void {
    if (data.access != null) {
      // Use SetTokens instead of Login to avoid @bo/ng-auth's automatic navigation
      this.store.dispatch(new SetTokens(data));
      this.store.dispatch(new SetSubscriptionStateFromUserLogin());

      // Determine the target URL (returnUrl or default dashboard)
      const targetUrl = this.returnUrl || '/dashboard';

      // Load state FIRST, then navigate
      // This ensures all guards have the data they need when we navigate
      this.subscriptions.add(
        this.store.dispatch(new NavigateToDashboardFromLogin()).subscribe({
          next: () => {
            this.router.navigateByUrl(targetUrl);
          },
          error: () => {
            // Navigate anyway on error
            this.router.navigateByUrl(targetUrl);
          },
        }),
      );
    }
  }

  protected override handleSubmitError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      const message = error.error!['detail'];
      this.form.setErrors({ server: message });
      return;
    }
    super.handleSubmitError(error);
  }

  protected onForgotPassword() {
    const email = this.form.get('username')?.value;
    if (email) this.queryParams = this.queryParams.set('email', email);
    this.router.navigate(['/forgot-password'], {
      queryParams: updateParamsObject(this.queryParams),
    });
  }

  submitData(): Observable<TokenResponse> {
    return this.authService.login(this.form.value.username, this.form.value.password);
  }

  protected readonly InputType = InputType;
}
